import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Snackbar,
  Typography
} from '@mui/material';

// Import icons
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import LockIcon from '@mui/icons-material/Lock';
import PeopleIcon from '@mui/icons-material/People';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import SearchOffIcon from '@mui/icons-material/SearchOff';

// Import services, types, and utilities
import { PayrollMonthService } from '../../services/payrollService';
import { PayrollMonth, PayrollStatus } from '../../types/payroll';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { useRole } from '../../hooks/useRole';

// Import reusable components
import PageHeader from '../common/PageHeader';
import DataGrid, { Column } from '../common/DataGrid';
import FilterBar, { FilterConfig } from '../common/FilterBar';
import StatsSummary from '../common/StatsSummary';
import ConfirmDialog from '../common/ConfirmDialog';

// Import payroll styles
import '../../styles/Payroll.css';

const PayrollMonthList: React.FC = () => {
  // State for payroll months data
  const [payrollMonths, setPayrollMonths] = useState<PayrollMonth[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [initialized, setInitialized] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  // Pagination state
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  
  // Filter state
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [yearFilter, setYearFilter] = useState<string>('all');
  
  // Dialog state
  const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);
  const [confirmDialogTitle, setConfirmDialogTitle] = useState<string>('');
  const [confirmDialogContent, setConfirmDialogContent] = useState<string>('');
  const [confirmDialogAction, setConfirmDialogAction] = useState<() => Promise<void>>(() => Promise.resolve());
  const [confirmDialogColor, setConfirmDialogColor] = useState<'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'>('primary');
  
  // Selected payroll month for actions
  const [selectedMonth, setSelectedMonth] = useState<PayrollMonth | null>(null);
  
  // Action menu state
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null);
  const [actionMenuMonthId, setActionMenuMonthId] = useState<number | null>(null);
  
  // Notification state
  const [notification, setNotification] = useState<{
    message: string;
    type: 'success' | 'error' | 'warning';
    show: boolean;
  }>({
    message: '',
    type: 'success',
    show: false
  });
  
  // Ref to prevent duplicate initialization
  const initializationRef = useRef<boolean>(false);
  
  const navigate = useNavigate();
  const { hasAnyRole } = useRole();
  
  // Role-based permissions
  const canManagePayroll = hasAnyRole(['admin']);
  const canApprovePayroll = hasAnyRole(['admin']);
  
  // Helper function to format month display
  const formatMonthDisplay = (month: PayrollMonth): string => {
    if (month.period_year && month.period_month) {
      const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];
      return `${monthNames[month.period_month - 1]} ${month.period_year}`;
    }
    // Fallback to period format (YYYY-MM)
    if (month.period) {
      const [year, monthNum] = month.period.split('-');
      const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];
      return `${monthNames[parseInt(monthNum) - 1]} ${year}`;
    }
    return 'Unknown Period';
  };
  
  // Notification helper function
  const showNotification = (message: string, type: 'success' | 'error' | 'warning' = 'success') => {
    setNotification({ message, type, show: true });
    setTimeout(() => {
      setNotification(prev => ({ ...prev, show: false }));
    }, 4000);
  };

  // Close notification manually
  const closeNotification = () => {
    setNotification(prev => ({ ...prev, show: false }));
  };

  // Initialize data function
  const initializeData = async () => {
    if (initializationRef.current) return;
    initializationRef.current = true;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await PayrollMonthService.getAllPayrollMonths();
      setPayrollMonths(response.data || []);
      setInitialized(true);
      
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load payroll months';
      setError(errorMessage);
      showNotification(errorMessage, 'error');
      console.error('Error fetching payroll months:', err);
    } finally {
      setLoading(false);
    }
  };

  // Refresh data function
  const refreshData = async () => {
    initializationRef.current = false;
    await initializeData();
  };

  useEffect(() => {
    initializeData();
  }, []);

  // Pagination handlers
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Action handlers
  const handleAddPayrollMonth = () => {
    navigate('/payroll/months/new');
  };

  const handleEditPayrollMonth = (id: number) => {
    navigate(`/payroll/months/${id}/edit`);
  };

  const handleViewPayrollMonth = (id: number) => {
    navigate(`/payroll/months/${id}`);
  };

  const handleManageEmployees = (id: number) => {
    navigate(`/payroll/months/${id}/employees`);
  };

  const handleViewReports = (id: number) => {
    navigate(`/payroll/months/${id}/reports`);
  };

  // Status change handlers
  const handleSubmitPayroll = (month: PayrollMonth) => {
    setSelectedMonth(month);
    setConfirmDialogTitle('Submit Payroll Month');
    setConfirmDialogContent(`Are you sure you want to submit payroll month ${formatMonthDisplay(month)} for approval? This will lock the payroll data for review.`);
    setConfirmDialogAction(async () => {
      try {
        await PayrollMonthService.updatePayrollMonthStatus(month.id, 'SUBMITTED');
        showNotification(`Payroll month ${formatMonthDisplay(month)} has been submitted for approval.`, 'success');
        refreshData();
      } catch (err: any) {
        showNotification(`Failed to submit payroll: ${err.message}`, 'error');
      }
    });
    setConfirmDialogColor('primary');
    setConfirmDialogOpen(true);
  };

  const handleApprovePayroll = (month: PayrollMonth) => {
    setSelectedMonth(month);
    setConfirmDialogTitle('Approve Payroll Month');
    setConfirmDialogContent(`Are you sure you want to approve payroll month ${formatMonthDisplay(month)}? This will finalize the payroll data and cannot be easily undone.`);
    setConfirmDialogAction(async () => {
      try {
        await PayrollMonthService.updatePayrollMonthStatus(month.id, 'APPROVED');
        showNotification(`Payroll month ${formatMonthDisplay(month)} has been approved.`, 'success');
        refreshData();
      } catch (err: any) {
        showNotification(`Failed to approve payroll: ${err.message}`, 'error');
      }
    });
    setConfirmDialogColor('success');
    setConfirmDialogOpen(true);
  };

  const handleRevertPayroll = (month: PayrollMonth) => {
    setSelectedMonth(month);
    setConfirmDialogTitle('Revert Payroll Month');
    setConfirmDialogContent(`Are you sure you want to revert payroll month ${formatMonthDisplay(month)} back to draft status? This will allow further editing.`);
    setConfirmDialogAction(async () => {
      try {
        await PayrollMonthService.updatePayrollMonthStatus(month.id, 'DRAFT');
        showNotification(`Payroll month ${month.period} has been reverted to draft status.`, 'success');
        refreshData();
      } catch (err: any) {
        showNotification(`Failed to revert payroll: ${err.message}`, 'error');
      }
    });
    setConfirmDialogColor('warning');
    setConfirmDialogOpen(true);
  };

  const handleDeletePayrollMonth = (month: PayrollMonth) => {
    setSelectedMonth(month);
    setConfirmDialogTitle('Delete Payroll Month');
    setConfirmDialogContent(`Are you sure you want to delete payroll month ${month.period}? This action cannot be undone.`);
    setConfirmDialogAction(async () => {
      try {
        await PayrollMonthService.deletePayrollMonth(month.id);
        showNotification(`Payroll month ${month.period} has been deleted.`, 'success');
        refreshData();
      } catch (err: any) {
        showNotification(`Failed to delete payroll: ${err.message}`, 'error');
      }
    });
    setConfirmDialogColor('error');
    setConfirmDialogOpen(true);
  };

  // Dialog handlers
  const handleConfirmAction = async () => {
    setConfirmDialogOpen(false);
    await confirmDialogAction();
  };

  const handleCancelAction = () => {
    setConfirmDialogOpen(false);
  };

  // Action menu handlers
  const handleOpenActionMenu = (event: React.MouseEvent<HTMLElement>, monthId: number) => {
    setActionMenuAnchor(event.currentTarget);
    setActionMenuMonthId(monthId);
  };

  const handleCloseActionMenu = () => {
    setActionMenuAnchor(null);
    setActionMenuMonthId(null);
  };

  // Filter payroll months based on search term and filters
  const filteredPayrollMonths = payrollMonths
    .filter(month => {
      // Search term filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch = 
          month.period.toLowerCase().includes(searchLower) ||
          (month.period_year?.toString() || '').includes(searchLower) ||
          month.status.toLowerCase().includes(searchLower);
          
        if (!matchesSearch) return false;
      }
      
      // Status filter
      if (statusFilter !== 'all' && month.status !== statusFilter) {
        return false;
      }
      
      // Year filter
      if (yearFilter !== 'all' && (month.period_year?.toString() || '') !== yearFilter) {
        return false;
      }
      
      return true;
    })
    .sort((a, b) => {
      // Sort by year desc, then by month number desc
      if (a.period_year !== b.period_year) {
        return (b.period_year || 0) - (a.period_year || 0);
      }
      // Use period_month if available, otherwise default to 0
      const aMonthNum = a.period_month || 0;
      const bMonthNum = b.period_month || 0;
      return bMonthNum - aMonthNum;
    });
  
  // Get unique years for filter
  const years = Array.from(new Set(payrollMonths.filter(month => month.period_year !== undefined).map(month => month.period_year)))
    .sort((a, b) => (b || 0) - (a || 0)); // Sort years descending
  
  // Status chip color mapping
  const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (status) {
      case 'DRAFT':
        return 'default';
      case 'PROCESSING':
        return 'info';
      case 'SUBMITTED':
        return 'warning';
      case 'APPROVED':
        return 'success';
      case 'REJECTED':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusClass = (status: string): string => {
    switch (status) {
      case 'DRAFT':
        return 'payroll-status-draft';
      case 'PROCESSING':
        return 'payroll-status-processing';
      case 'SUBMITTED':
        return 'payroll-status-submitted';
      case 'APPROVED':
        return 'payroll-status-approved';
      case 'REJECTED':
        return 'payroll-status-rejected';
      default:
        return 'payroll-status-draft';
    }
  };

  const columns: Column<PayrollMonth>[] = [
    {
      id: 'month_name',
      label: 'Month',
      format: (value) => {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center' }} className="payroll-page">
            <CalendarMonthIcon sx={{ mr: 1, color: 'primary.main' }} />
            <span>{value}</span>
          </Box>
        );
      }
    },
    {
      id: 'year',
      label: 'Year',
      format: (value) => value
    },
    {
      id: 'status',
      label: 'Status',
      format: (value) => {
        const status = value as PayrollStatus;
        return (
          <Chip
            size="small"
            label={status}
            color={getStatusColor(status)}
            className={getStatusClass(status)}
          />
        );
      }
    },
    {
      id: 'employee_count',
      label: 'Employees',
      format: (value) => value || 0
    },
    {
      id: 'total_amount',
      label: 'Total Amount',
      format: (value) => formatCurrency(value || 0)
    },
    {
      id: 'actions',
      label: 'Actions',
      format: (_, row) => (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Tooltip title="View Details">
            <IconButton 
              size="small" 
              onClick={() => handleViewPayrollMonth(row.id)}
              className="payroll-action-button"
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          
          {canManagePayroll && row.status === 'DRAFT' && (
            <Tooltip title="Edit">
              <IconButton 
                size="small" 
                onClick={() => handleEditPayrollMonth(row.id)}
                className="payroll-action-button"
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          
          <Tooltip title="More Actions">
            <IconButton 
              size="small" 
              onClick={(e) => handleOpenActionMenu(e, row.id)}
              className="payroll-action-button"
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  const filterConfigs: FilterConfig[] = [
    {
      id: 'status',
      label: 'Status',
      value: statusFilter,
      options: [
        { value: 'all', label: 'All Status' },
        { value: 'DRAFT', label: 'Draft' },
        { value: 'PROCESSING', label: 'Processing' },
        { value: 'SUBMITTED', label: 'Submitted' },
        { value: 'APPROVED', label: 'Approved' },
        { value: 'REJECTED', label: 'Rejected' }
      ]
    },
    {
      id: 'year',
      label: 'Year',
      value: yearFilter,
      options: [
        { value: 'all', label: 'All Years' },
        ...years.map(year => ({
          value: year ? year.toString() : '',
          label: year ? year.toString() : ''
        }))
      ]
    }
  ];

  const stats = [
    {
      label: 'Total Payroll Months',
      value: payrollMonths.length
    },
    {
      label: 'Draft',
      value: payrollMonths.filter(m => m.status === 'DRAFT').length,
      color: 'text.secondary'
    },
    {
      label: 'Submitted',
      value: payrollMonths.filter(m => m.status === 'SUBMITTED').length,
      color: 'primary.main'
    },
    {
      label: 'Approved',
      value: payrollMonths.filter(m => m.status === 'APPROVED').length,
      color: 'success.main'
    }
  ];

  return (
    <Box className="payroll-container" sx={{ p: 2 }}>
      {/* Header Section */}
      <PageHeader 
        title="Payroll Months" 
        subtitle="Manage monthly payroll periods"
        icon={<CalendarMonthIcon />}
        onRefresh={refreshData}
        refreshDisabled={loading}
        compact={true}
        actions={
          canManagePayroll && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddPayrollMonth}
              disabled={!canManagePayroll}
              className="payroll-button payroll-button-primary"
            >
              Add Payroll Month
            </Button>
          )
        }
      />

      {/* Error Display */}
      {error && !loading && (
        <Alert 
          severity="error" 
          onClose={() => setError(null)}
          sx={{ mb: 2 }}
        >
          {error}
        </Alert>
      )}

      {/* Search and Filter Section */}
      <FilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search by month name, year, or status"
        filters={filterConfigs}
        onFilterChange={(filterId, value) => {
          if (filterId === 'status') setStatusFilter(value);
          if (filterId === 'year') setYearFilter(value);
        }}
        compact={true}
      />

      {/* Data Grid */}
      <Box className="payroll-card">
        <DataGrid
          columns={columns as Column<unknown>[]}
          rows={filteredPayrollMonths}
          keyField="id"
          loading={loading}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          emptyMessage="No payroll months found"
          emptyIcon={<SearchOffIcon />}
          maxHeight="calc(100vh - 320px)"
        />
      </Box>

      {/* Summary Section */}
      <StatsSummary
        stats={stats}
      />

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleCloseActionMenu}
        className="payroll-action-menu"
      >
        {(() => {
          const month = payrollMonths.find(m => m.id === actionMenuMonthId);
          if (!month) return null;
          
          return (
            <>
              {/* View action */}
              <MenuItem onClick={() => {
                handleCloseActionMenu();
                handleViewPayrollMonth(month.id);
              }}>
                <ListItemIcon>
                  <VisibilityIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>View Details</ListItemText>
              </MenuItem>
              
              {/* Edit action */}
              {canManagePayroll && month.status === 'DRAFT' && (
                <MenuItem onClick={() => {
                  handleCloseActionMenu();
                  handleEditPayrollMonth(month.id);
                }}>
                  <ListItemIcon>
                    <EditIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Edit</ListItemText>
                </MenuItem>
              )}
              
              {/* Manage employees action */}
              {canManagePayroll && ['DRAFT', 'PROCESSING'].includes(month.status) && (
                <MenuItem onClick={() => {
                  handleCloseActionMenu();
                  handleManageEmployees(month.id);
                }}>
                  <ListItemIcon>
                    <PeopleIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Manage Employees</ListItemText>
                </MenuItem>
              )}
              
              {/* View reports action */}
              <MenuItem onClick={() => {
                handleCloseActionMenu();
                handleViewReports(month.id);
              }}>
                <ListItemIcon>
                  <ReceiptIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>View Reports</ListItemText>
              </MenuItem>
              
              {/* Status change actions */}
              {canManagePayroll && month.status === 'DRAFT' && (
                <MenuItem onClick={() => {
                  handleCloseActionMenu();
                  handleSubmitPayroll(month);
                }}>
                  <ListItemIcon>
                    <PlayArrowIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText>Submit for Approval</ListItemText>
                </MenuItem>
              )}
              
              {canApprovePayroll && month.status === 'SUBMITTED' && (
                <MenuItem onClick={() => {
                  handleCloseActionMenu();
                  handleApprovePayroll(month);
                }}>
                  <ListItemIcon>
                    <CheckCircleIcon fontSize="small" color="success" />
                  </ListItemIcon>
                  <ListItemText>Revert to Draft</ListItemText>
                </MenuItem>
              )}
              
              {/* Delete action */}
              {canManagePayroll && month.status === 'DRAFT' && (
                <MenuItem onClick={() => {
                  handleCloseActionMenu();
                  handleDeletePayrollMonth(month);
                }}>
                  <ListItemIcon>
                    <DeleteIcon fontSize="small" color="error" />
                  </ListItemIcon>
                  <ListItemText>Delete</ListItemText>
                </MenuItem>
              )}
            </>
          );
        })()}
      </Menu>

      {/* Confirmation Dialog */}
      <ConfirmDialog
        open={confirmDialogOpen}
        title={confirmDialogTitle}
        content={confirmDialogContent}
        onConfirm={handleConfirmAction}
        onCancel={handleCancelAction}
        confirmColor={confirmDialogColor}
      />

      {/* Notification */}
      {notification.show && (
        <div className={`payroll-notification payroll-notification-${notification.type}`}>
          <div className="payroll-notification-content">{notification.message}</div>
          <button className="payroll-notification-close" onClick={closeNotification} title="Close notification" aria-label="Close notification">
            <CancelIcon fontSize="small" />
          </button>
        </div>
      )}
    </Box>
  );
};

export default PayrollMonthList;
